import { useCallback, useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import dayjs, { Dayjs } from "dayjs";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Chip from "@mui/material/Chip";
import Paper from "@mui/material/Paper";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import SendIcon from "@mui/icons-material/Send";
import StoreIcon from "@mui/icons-material/Store";
import UndoIcon from "@mui/icons-material/Undo";
import ExportButton from "../components/ExportButton";
import PurchaseOrderDetailDrawer from "../components/PurchaseOrder/DetailDrawer";
import PurchaseOrderFilter from "../components/PurchaseOrder/Filter";
import PublishDialog from "../components/PurchaseOrder/PublishDialog";
import SetBranchDialog from "../components/PurchaseOrder/SetBranchDialog";
import StatusSummaryBar from "../components/PurchaseOrder/StatusSummaryBar";
import StatusTaskBanner from "../components/PurchaseOrder/StatusTaskBanner";
import PurchaseOrderTable from "../components/PurchaseOrder/Table";
import UnpublishDialog from "../components/PurchaseOrder/UnpublishDialog";
import {
  isBranchAssignable,
  isPublishable,
  isRecallable,
  lockedReason,
  PO_API_DATE_FORMAT,
  PO_DELIVERY_RANGE_BACK_DAYS,
  PO_DELIVERY_RANGE_FORWARD_DAYS,
  PO_LIST_BATCH_QUERY_KEY,
  PO_LIST_STATUS_QUERY_KEY,
  PO_SEARCH_DEBOUNCE_MS,
  PO_UNFILTERED,
  toChainFilter,
  toStatusFilter,
} from "../components/PurchaseOrder/data";
import { usePurchaseOrderList } from "../components/PurchaseOrder/usePurchaseOrderList";
import { exportPurchaseOrders } from "../services";
import {
  PurchaseOrderListFilter,
  PurchaseOrderStatus,
} from "../types/purchase-order.type";
import { numberWithCommas } from "../utils";
import { useDebounce } from "../utils/hooks/useDebounce";

/** ตัวกรอง "เฉพาะ PO จากไฟล์นำเข้าครั้งนี้" ที่จอ W2 ส่งต่อมาทาง query string */
const BATCH_QUERY_KEY = PO_LIST_BATCH_QUERY_KEY;

/** สถานะที่แถบ "ยังไม่ระบุสาขา" ใช้กรอง — งานที่ค้างอยู่ที่ HO */
const NO_BRANCH_STATUS: PurchaseOrderStatus = "NO_BRANCH";

/** สถานะที่แถบ "สาขาส่งคืน" ใช้กรอง — งานที่สาขาตีกลับมาที่ HO */
const RETURNED_STATUS: PurchaseOrderStatus = "RETURNED";

/**
 * W1 รายการ PO (HO)
 *
 * เห็นทุกสาขา · action คือส่งให้สาขา, ระบุ/ย้ายสาขา (รวมใบที่สาขาส่งคืน) และดึงกลับจาก
 * สาขา · ใบที่ใช้แล้วหรือหมดช่วงใช้ติ๊กไม่ได้ พร้อม tooltip บอกเหตุผล · Export Excel ได้
 * ตามตัวกรองเดียวกับตาราง
 * คอลัมน์สายอ่านจากสายหลักของร้าน แก้จากจอนี้ไม่ได้ และการ์ดแดง "ยังไม่จับคู่ร้าน"
 * บอกว่าค้างกี่ใบเท่านั้น ไม่ได้บล็อกการส่งให้สาขา
 */
export default function PurchaseOrderListPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const batchUuid = searchParams.get(BATCH_QUERY_KEY) ?? "";

  const [dateFrom, setDateFrom] = useState<Dayjs>(() =>
    dayjs().startOf("day").subtract(PO_DELIVERY_RANGE_BACK_DAYS, "day"),
  );
  const [dateTo, setDateTo] = useState<Dayjs>(() =>
    dayjs().startOf("day").add(PO_DELIVERY_RANGE_FORWARD_DAYS, "day"),
  );
  const [companyId, setCompanyId] = useState<string>(PO_UNFILTERED);
  const [chain, setChain] = useState<string>(PO_UNFILTERED);
  const [status, setStatus] = useState<string>(
    () =>
      toStatusFilter(searchParams.get(PO_LIST_STATUS_QUERY_KEY) ?? "") ??
      PO_UNFILTERED,
  );
  const [search, setSearch] = useState<string>("");
  const [selectedUuids, setSelectedUuids] = useState<Set<string>>(
    () => new Set(),
  );
  const [detailUuid, setDetailUuid] = useState<string | null>(null);
  const [publishOpen, setPublishOpen] = useState<boolean>(false);
  const [setBranchOpen, setSetBranchOpen] = useState<boolean>(false);
  const [unpublishOpen, setUnpublishOpen] = useState<boolean>(false);

  const debouncedSearch = useDebounce(search, PO_SEARCH_DEBOUNCE_MS);

  // ตัวกรองชุดเดียวของตารางและของ Export ไฟล์ที่ดาวน์โหลดจึงเป็นสิ่งเดียวกับที่เห็นบนจอ
  const filter = useMemo<PurchaseOrderListFilter>(
    () => ({
      delivery_from: dateFrom.format(PO_API_DATE_FORMAT),
      delivery_to: dateTo.format(PO_API_DATE_FORMAT),
      company_id: companyId === PO_UNFILTERED ? undefined : companyId,
      chain: toChainFilter(chain),
      status: toStatusFilter(status),
      q: debouncedSearch || undefined,
      batch_uuid: batchUuid || undefined,
    }),
    [dateFrom, dateTo, companyId, chain, status, debouncedSearch, batchUuid],
  );

  const list = usePurchaseOrderList(filter);
  const { rows, reload } = list;

  // ติ๊กเลือกได้เฉพาะใบในหน้าที่แสดงอยู่ · พอชุดแถวเปลี่ยน (เปลี่ยนหน้า เปลี่ยนตัวกรอง
  // หรืออ่านซ้ำ) ของที่เลือกไว้ก็ไม่อยู่บนจอแล้ว จึงต้องล้างทิ้ง
  useEffect(() => {
    setSelectedUuids(new Set());
  }, [rows]);

  // ชุดที่เลือกไว้ชุดเดียว แต่ละปุ่มนับเฉพาะใบที่ตัวเองทำได้
  const selectedRows = useMemo(
    () => rows.filter((row) => selectedUuids.has(row.uuid)),
    [rows, selectedUuids],
  );
  const selectedPurchaseOrders = useMemo(
    () => selectedRows.filter(isPublishable),
    [selectedRows],
  );
  const selectedAssignableCount = useMemo(
    () => selectedRows.filter(isBranchAssignable).length,
    [selectedRows],
  );
  const selectedRecallable = useMemo(
    () => selectedRows.filter(isRecallable),
    [selectedRows],
  );

  const showingOnlyNoBranch = status === NO_BRANCH_STATUS;
  const showingOnlyReturned = status === RETURNED_STATUS;

  const selection = useMemo(
    () => ({
      selected: selectedUuids,
      onChange: setSelectedUuids,
      canSelect: isBranchAssignable,
      disabledReason: lockedReason,
    }),
    [selectedUuids],
  );

  // W1 เป็นจอของ HO (route กั้นด้วย poManage = HO accounting) จึงเลือกดูสาขาใดก็ได้
  const branchFilter = useMemo(
    () => ({ companyId, onChange: setCompanyId }),
    [companyId],
  );

  const handleChangeDateRange = useCallback((from: Dayjs, to: Dayjs) => {
    setDateFrom(from);
    setDateTo(to);
  }, []);

  const handleClearBatchFilter = () => {
    const next = new URLSearchParams(searchParams);
    next.delete(BATCH_QUERY_KEY);
    next.delete(PO_LIST_STATUS_QUERY_KEY);
    setSearchParams(next);
  };

  const handleToggleNoBranchOnly = () => {
    setStatus(showingOnlyNoBranch ? PO_UNFILTERED : NO_BRANCH_STATUS);
  };

  const handleToggleReturnedOnly = () => {
    setStatus(showingOnlyReturned ? PO_UNFILTERED : RETURNED_STATUS);
  };

  const handleOpenUnpublish = () => {
    setUnpublishOpen(true);
  };

  const handleCloseUnpublish = () => {
    setUnpublishOpen(false);
  };

  const handleOpenSetBranch = () => {
    setSetBranchOpen(true);
  };

  const handleCloseSetBranch = () => {
    setSetBranchOpen(false);
  };

  const handleBranchSet = () => {
    reload(true);
  };

  // จับคู่ร้านใน drawer (HO) แล้วอ่านรายการซ้ำแบบไม่กระพริบ ให้สถานะในตารางตรงกับของจริง
  const handleDrawerChanged = useCallback(() => {
    reload(false);
  }, [reload]);

  const headOfficeDrawer = useMemo(
    () => ({ onChanged: handleDrawerChanged }),
    [handleDrawerChanged],
  );

  const handleExport = () => exportPurchaseOrders(filter);

  const handleOpenDetail = (uuid: string) => {
    setDetailUuid(uuid);
  };

  const handleCloseDetail = () => {
    setDetailUuid(null);
  };

  const handleOpenPublish = () => {
    setPublishOpen(true);
  };

  const handleClosePublish = () => {
    setPublishOpen(false);
  };

  const handlePublished = () => {
    reload(true);
  };

  return (
    <Box sx={{ padding: "8px 0" }}>
      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="flex-start"
        flexWrap="wrap"
        gap={1}
        mb={2}
      >
        <Box>
          <Typography variant="h6" fontWeight={700}>
            รายการ PO
          </Typography>
          <Typography variant="body2" color="text.secondary">
            PO โมเดิร์นเทรดทุกสาขา
          </Typography>
        </Box>

        <Stack direction="row" alignItems="center" gap={1} flexWrap="wrap">
          <ExportButton
            request={handleExport}
            fallbackFilename={`purchase-orders_${dayjs().format("YYYYMMDD")}.xlsx`}
            disabled={list.totalCount === 0}
          />
          <Button
            variant="outlined"
            color="warning"
            startIcon={<UndoIcon />}
            disabled={selectedRecallable.length === 0}
            onClick={handleOpenUnpublish}
          >
            ดึงกลับ ({selectedRecallable.length})
          </Button>
          <Button
            variant="outlined"
            startIcon={<StoreIcon />}
            disabled={selectedAssignableCount === 0}
            onClick={handleOpenSetBranch}
          >
            ระบุ/ย้ายสาขา ({selectedAssignableCount})
          </Button>
          <Button
            variant="contained"
            startIcon={<SendIcon />}
            disabled={selectedPurchaseOrders.length === 0}
            onClick={handleOpenPublish}
          >
            ส่งให้สาขา ({selectedPurchaseOrders.length})
          </Button>
        </Stack>
      </Stack>

      <StatusTaskBanner
        count={list.summary.NO_BRANCH}
        showingOnly={showingOnlyNoBranch}
        onToggleShowingOnly={handleToggleNoBranchOnly}
        severity="warning"
      >
        มี {numberWithCommas(list.summary.NO_BRANCH)} ใบยังไม่ระบุสาขา —
        ติ๊กเลือกแล้วกด ระบุ/ย้ายสาขา ใบกลุ่มนี้จะยังไม่ถูกส่งให้สาขาจนกว่าจะระบุ
      </StatusTaskBanner>

      <StatusTaskBanner
        count={list.summary.RETURNED}
        showingOnly={showingOnlyReturned}
        onToggleShowingOnly={handleToggleReturnedOnly}
        severity="warning"
      >
        สาขาส่งคืน {numberWithCommas(list.summary.RETURNED)} ใบ —
        ดูเหตุผลที่แถว แล้วย้ายสาขาหรือส่งกลับไปใหม่
      </StatusTaskBanner>

      <StatusSummaryBar
        summary={list.summary}
        activeStatus={status}
        onSelectStatus={setStatus}
      />

      <Paper sx={{ padding: "24px 16px 8px", mb: 2 }}>
        <PurchaseOrderFilter
          dateFrom={dateFrom}
          dateTo={dateTo}
          onChangeDateRange={handleChangeDateRange}
          chain={chain}
          onChangeChain={setChain}
          status={status}
          onChangeStatus={setStatus}
          search={search}
          onChangeSearch={setSearch}
          branchFilter={branchFilter}
        />
        {batchUuid && (
          <Chip
            label="เฉพาะ PO จากไฟล์นำเข้าที่เลือก"
            onDelete={handleClearBatchFilter}
            sx={{ mb: 2 }}
          />
        )}
      </Paper>

      <PurchaseOrderTable
        rows={rows}
        loading={list.loading}
        totalCount={list.totalCount}
        page={list.page}
        limit={list.limit}
        onChangePage={list.changePage}
        onChangeLimit={list.changeLimit}
        onOpenDetail={handleOpenDetail}
        selection={selection}
      />

      <PublishDialog
        open={publishOpen}
        purchaseOrders={selectedPurchaseOrders}
        onClose={handleClosePublish}
        onPublished={handlePublished}
      />

      <SetBranchDialog
        open={setBranchOpen}
        purchaseOrders={selectedRows}
        onClose={handleCloseSetBranch}
        onUpdated={handleBranchSet}
      />

      <UnpublishDialog
        open={unpublishOpen}
        purchaseOrders={selectedRecallable}
        onClose={handleCloseUnpublish}
        onUnpublished={handleBranchSet}
      />

      <PurchaseOrderDetailDrawer
        purchaseOrderUuid={detailUuid}
        onClose={handleCloseDetail}
        headOffice={headOfficeDrawer}
      />
    </Box>
  );
}
